<template>
  <div class="accountPassword">
    <account-header />
    <v-divider class="accountPassword_divider" />
    <h3 class="accountPassword_title">
      現在のパスワードと新しいパスワードを入力してください。
    </h3>
    <ValidationObserver ref="observer" v-slot="{ invalid }">
      <section class="accountPassword_content">
        <ValidationProvider
          v-slot="{ errors }"
          vid="oldPassword"
          name="現在のパスワード"
          class="accountPassword_input"
        >
          <h4 class="accountPassword_item">
            <span>現在のパスワード</span>
            <em>必須</em>
          </h4>
          <app-text-field
            ref="oldPassword"
            v-model="oldPassword"
            :type="showOldPassword ? 'text' : 'password'"
            :append-icon="
              showOldPassword ? 'icon icon-eye' : 'icon icon-eye-closed'
            "
            class="accountPassword_container_input"
            :error-messages="errors"
            color="dark"
            required
            aria-autocomplete="off"
            autocomplete="off"
            full-width
            outlined
            @click:append="showOldPassword = !showOldPassword"
          />
        </ValidationProvider>
        <ValidationProvider
          v-slot="{ errors }"
          vid="newPassword"
          name="新しいパスワード"
          rules="passwordValidChars|minmax:8,20|passwordNumeric|passwordLowercase|passwordUppercase|symbol"
          class="accountPassword_input"
        >
          <h4
            class="accountPassword_item"
            :class="{ ['accountPassword_item--error']: errors.length }"
          >
            <span>新しいパスワード</span>
            <em>必須</em>
          </h4>

          <app-text-field
            ref="newPassword"
            v-model="newPassword"
            :type="showNewPassword ? 'text' : 'password'"
            :append-icon="
              showNewPassword ? 'icon icon-eye' : 'icon icon-eye-closed'
            "
            class="accountPassword_container_input"
            :error-messages="errors"
            color="dark"
            required
            aria-autocomplete="off"
            autocomplete="off"
            full-width
            outlined
            hint="半角英数字記号8文字以上"
            persistent-hint
            @click:append="showNewPassword = !showNewPassword"
          />
        </ValidationProvider>
        <ValidationProvider
          v-slot="{ errors }"
          vid="confirmPassword"
          name="新しいパスワード（確認用）"
          rules="password:@newPassword"
          class="accountPassword_input"
        >
          <h4
            class="accountPassword_item"
            :class="{ ['accountPassword_item--error']: errors.length }"
          >
            <span> 新しいパスワード（確認用）</span>
            <em>必須</em>
          </h4>

          <app-text-field
            ref="confirmPassword"
            v-model="confirmPassword"
            :type="showConfirmPassword ? 'text' : 'password'"
            :append-icon="
              showConfirmPassword ? 'icon icon-eye' : 'icon icon-eye-closed'
            "
            class="accountPassword_container_input"
            :error-messages="errors"
            color="dark"
            required
            aria-autocomplete="off"
            autocomplete="off"
            full-width
            outlined
            persistent-hint
            hint="確認の為、同じパスワードを再度入力してください。"
            @click:append="showConfirmPassword = !showConfirmPassword"
          />
        </ValidationProvider>
      </section>
      <div class="accountPassword_action">
        <v-btn
          class="accountPassword_btn-submit"
          :disabled="invalid || !allFieldsFilled"
          :loading="submitLoading"
          @click="handleSubmit"
        >
          新しいパスワードを登録
        </v-btn>
      </div>
    </ValidationObserver>
    <!-- Message dialog -->
    <clip-dialog-message
      :open="!!dialogMessageText"
      :message="dialogMessageText"
      @onClose="closeDialogMessage"
    />
    <!-- ----------------- -->
  </div>
</template>

<script>
import { authService } from '@/services/auth.service';
import { changePassword } from '@/services/password.service';
import AccountHeader from './AccountHeader.vue';
import ClipDialogMessage from '@/components/dialogs/ClipDialogMessage.vue';

export default {
  name: 'AccountPassword',
  components: {
    AccountHeader,
    ClipDialogMessage,
  },
  data() {
    return {
      submitLoading: false,
      showOldPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
      dialogMessageText: '',
    };
  },
  computed: {
    allFieldsFilled() {
      return !!(this.oldPassword && this.newPassword && this.confirmPassword);
    },
  },
  watch: {
    dialogMessageText(val) {
      val || this.closeDialogMessage();
    },
  },
  mounted() {},
  methods: {
    async handleSubmit() {
      try {
        this.$refs.observer.setErrors({
          oldPassword: [],
        });
        this.submitLoading = true;
        await changePassword(this.oldPassword, this.newPassword);
        this.oldPassword = '';
        this.newPassword = '';
        this.confirmPassword = '';
        this.$refs.observer.reset();
        this.dialogMessageText = '新しいパスワードを登録しました。';
      } catch (error) {
        console.error(error);
        this.$refs.observer.setErrors({
          oldPassword: [''],
        });
      } finally {
        this.submitLoading = false;
      }
    },
    closeDialogMessage() {
      this.dialogMessageText = null;
      authService.logout({ skipLogging: true });
    },
  },
};
</script>

<style lang="scss">
.accountPassword {
  overflow: auto;
  height: 100%;

  &_title {
    font-size: 14px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: normal;
    text-align: left;
    color: #4b4b4b;
    margin-top: 16px;
  }

  &_item {
    display: flex;
    justify-content: space-between;
    width: 295px;
    span,
    em {
      font-size: 14px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.5;
      letter-spacing: normal;
      text-align: left;
      color: #666;
      margin: 10px 0;
    }
    em {
      color: #eb545b;
    }
  }
  &_container {
    display: flex;
    flex-direction: column;
    &_item {
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
      height: 16px;
      color: #333333;
      &--error {
        color: var(--v-error-base);
      }
    }
    &_input {
      margin-bottom: 0;
      margin-top: 0;
      padding-top: 0;
      width: 295px;
      &.v-input {
        font-size: 18px;
        .v-messages:not(.error--text) {
          .v-messages__message {
            font-size: 12px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.5;
            letter-spacing: normal;
            text-align: left;
            color: #989898;
          }
        }
      }
      .v-text-field__slot {
        padding-left: 0;
      }

      .v-input__slot {
        min-height: 40px !important;
        margin-bottom: 2px;
        .v-input__append-inner {
          margin-top: 9px;
        }
        .v-message__message {
          font-size: 12px;
          font-weight: normal;
          font-stretch: normal;
          font-style: normal;
          line-height: 1.5;
          letter-spacing: normal;
          text-align: left;
          color: #989898;
        }
      }
      input {
        line-height: 32px !important;
        color: var(--text-color) !important;
        font-size: 18px !important;
        font-weight: normal !important;
        font-stretch: normal !important;
        font-style: normal;
        letter-spacing: normal;
        text-align: left;
        &:-webkit-autofill,
        &:-webkit-autofill::first-line {
          font-family: 'Noto Sans JP', sans-serif;
          font-size: 18px;
          font-weight: 500;
        }
      }

      .v-text-field__details {
        padding: 0 !important;
        padding-top: 0px !important;
        margin-bottom: 0 !important;
      }
    }
  }

  &_input {
    margin-top: 4px;
    &-label {
      font-size: 14px;
      font-weight: 500;
      font-stretch: normal;
      font-style: normal;
      letter-spacing: 0.7px;
      text-align: left;
      color: var(--text-color-2);
      margin-right: 20px;
    }
    &-code {
      &.v-text-field {
        max-width: 168px;
        font-size: 40px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        letter-spacing: 2px;
        text-align: center;
        color: var(--text-color);
        &.theme--light.v-input input {
          max-width: 168px;
          max-height: 70px;
          text-align: center;
        }
      }
    }
  }
  &_divider {
    margin-top: 16px;
    &.theme--light.v-divider {
      border-color: #ccc;
    }
  }
  &_gap-header {
    height: 26px;
  }
  &_action {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    border-top: 1px solid #ccc;
    border-bottom: 1px solid #ccc;
    margin-top: 15px;
    padding: 15px 0;
    h3 {
      font-size: 14px;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      letter-spacing: normal;
      text-align: left;
      color: #4b4b4b;
    }
  }
  &_btn {
    &-submit {
      &.theme--light.v-btn.v-btn--has-bg {
        height: 36px;
        border-radius: 5px;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16);
        background-color: #0075c2;
        font-size: 14px;
        font-weight: bold;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.14;
        letter-spacing: normal;
        text-align: left;
        color: #fff;
      }
      &.theme--light.v-btn.v-btn--disabled.v-btn--has-bg {
        background-color: #f3f3f3 !important;
        box-shadow: none;
        color: #ccc !important;
      }
    }
  }
}
</style>
